/**
 * Jul-2026 (User-Wunsch) — Rechnungs-Vorschau VOR der Rechnungserstellung.
 *
 * Zeigt alle Positionen des Auftrags zur Kontrolle („Sind alle Positionen
 * enthalten?") und bietet:
 *   • Zurück zum Bearbeiten (öffnet den Auftrag)
 *   • Nicht übernommene Zeiterfassung als AW-Position übernehmen (1 AW = 6 min)
 *   • Rechnung erstellen (finalisiert + PDF) oder als Entwurf ins Rechnungs-Center
 *   • Bei internen Positionen (Garantie/GW/Auslieferung): Aufteilung
 */
import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { X, Edit2, FileText, AlertTriangle, Timer, Loader2, Check, TicketPercent, Settings } from "lucide-react";

import api from "../../lib/api";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { fmtEur, ORDER_CATEGORY_META } from "./workshopConstants";
import { addressIssues } from "../../lib/addressCheck";
import { CustomerAddressQuickFix } from "../CustomerAddressQuickFix";

const CAT_LABEL = { customer: "Kunde", warranty: "Garantie", gw: "Gebrauchtwagen", internal: "Auslieferung" };

// Aug-2026 (User-Wunsch) — Zahlarten wie im Verkaufsprozess
const PAY_OPTIONS = [
    ["barzahlung", "Barzahlung"],
    ["ec", "EC-Karte"],
    ["ueberweisung", "Überweisung"],
    ["sepa", "SEPA-Einzug"],
    ["finanzierung_hausbank", "Finanzierung Hausbank"],
    ["finanzierung_santander", "Finanzierung Santander"],
    ["leasing_santander", "Leasing Santander"],
];

export default function InvoicePreviewModal({ order: initialOrder, customer, busy, onClose, onEdit, onConfirm }) {
    const [order, setOrder] = useState(initialOrder);
    const [timesheet, setTimesheet] = useState(null);
    const [awRate, setAwRate] = useState(null);
    const [takingTime, setTakingTime] = useState(false);
    // Jul-2026 (User-Wunsch) — Gutschein an der Werkstatt-Kasse einlösen:
    // Code wird hier geprüft und beim Abrechnen als Rabattposition eingelöst.
    const [voucherCode, setVoucherCode] = useState("");
    const [voucherInfo, setVoucherInfo] = useState(null); // geprüfter, gültiger Gutschein
    const [voucherChecking, setVoucherChecking] = useState(false);
    const [custVouchers, setCustVouchers] = useState([]);
    // Aug-2026 (User-Wunsch) — Zahlart ist PFLICHT, Standard immer EC-Karte;
    // wird auf der Rechnung als „Zahlung: …" eingedruckt.
    const [payCode, setPayCode] = useState("ec");
    // Zahnrad-Konfiguration: welche Zahlarten sichtbar + Druck-Text je Zahlart
    const [payCfg, setPayCfg] = useState(null);
    const [cfgOpen, setCfgOpen] = useState(false);
    const [cfgSaving, setCfgSaving] = useState(false);
    useEffect(() => {
        api.get("/invoices/payment-methods-config")
            .then((r) => setPayCfg(r.data))
            .catch(() => {
                setPayCfg({ enabled: PAY_OPTIONS.map(([k]) => k), print_texts: {} });
                toast.error("Zahlarten-Konfiguration konnte nicht geladen werden — Standardwerte aktiv.");
            });
    }, []);
    const enabledPayOptions = PAY_OPTIONS.filter(([k]) => !payCfg?.enabled || payCfg.enabled.includes(k));
    const savePayCfg = async () => {
        setCfgSaving(true);
        try {
            const r = await api.put("/invoices/payment-methods-config", {
                enabled: payCfg.enabled, print_texts: payCfg.print_texts,
            });
            setPayCfg((c) => ({ ...c, enabled: r.data.enabled, print_texts: r.data.print_texts }));
            toast.success("Zahlarten-Einstellungen gespeichert");
            setCfgOpen(false);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Speichern fehlgeschlagen");
        } finally { setCfgSaving(false); }
    };

    useEffect(() => {
        if (!initialOrder.customer_id) return;
        api.get(`/dealer/vouchers/by-customer/${initialOrder.customer_id}`)
            .then((r) => setCustVouchers(r.data?.vouchers || []))
            .catch(() => setCustVouchers([]));
    }, [initialOrder.customer_id]);

    // Nov-2026 (User-Wunsch) — Werkstatt-Servicepreise (Aktionspreise aus der
    // Warenwirtschaft) direkt als eigener Gesamtbetrag übernehmbar.
    const [servicePrices, setServicePrices] = useState([]);
    useEffect(() => {
        api.get("/articles", { params: { category: "werkstatt_service", limit: 60 } })
            .then((r) => {
                const arr = Array.isArray(r.data) ? r.data : (r.data?.items || []);
                setServicePrices(arr.filter((a) => Number(a.sell_price_gross) > 0));
            })
            .catch(() => setServicePrices([]));
    }, []);

    const checkVoucher = useCallback(async (code) => {
        const c = String(code || "").trim().toUpperCase();
        if (!c) { setVoucherInfo(null); return; }
        setVoucherChecking(true);
        try {
            const { data } = await api.get(`/dealer/vouchers/validate`, { params: { code: c } });
            if (data.valid) {
                setVoucherInfo({ code: c, discount_type: data.discount_type, discount_value: Number(data.discount_value) || 0 });
            } else {
                setVoucherInfo(null);
                const msg = { pending_approval: "Gutschein wartet auf Freigabe.", rejected: "Gutschein wurde abgelehnt.", redeemed: "Gutschein wurde bereits eingelöst.", expired: "Gutschein ist abgelaufen.", not_found: "Gutschein-Code nicht gefunden." }[data.reason] || "Gutschein ungültig.";
                toast.error(msg);
            }
        } catch { toast.error("Gutschein konnte nicht geprüft werden."); }
        setVoucherChecking(false);
    }, []);

    // Auto-Prüfung beim Eintippen (Format GEB-/GUT-XXXXXX)
    useEffect(() => {
        const c = voucherCode.trim().toUpperCase();
        if (voucherInfo?.code === c) return;
        if (!/^[A-Z]{2,4}-[A-Z0-9]{6}$/.test(c)) { if (voucherInfo) setVoucherInfo(null); return; }
        const t = setTimeout(() => checkVoucher(c), 400);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [voucherCode]);

    const reloadOrder = useCallback(async () => {
        try {
            const r = await api.get(`/work-orders/${initialOrder.id}`);
            setOrder(r.data);
        } catch { /* Vorschau bleibt auf Stand */ }
    }, [initialOrder.id]);

    useEffect(() => {
        api.get(`/work-orders/${initialOrder.id}/timesheet`)
            .then((r) => setTimesheet(r.data))
            .catch(() => setTimesheet(null));
        api.get("/wms/montage-rate").then((r) => {
            const a = r.data;
            if (!a) return;
            const net = Number(a.sell_price_net || ((a.sell_price_gross || 0) / (1 + (a.vat_rate || 19) / 100)) || 0);
            if (net > 0) setAwRate(Math.round(net * 100) / 100);
        }).catch(() => {});
    }, [initialOrder.id]);

    const positions = order.positions || [];
    const lineNet = (p) => (Number(p.quantity) || 0) * (Number(p.unit_price_net) || 0) * (1 - (Number(p.discount_percent) || 0) / 100);
    const lineGross = (p) => lineNet(p) * (1 + (Number(p.vat_rate) || 0) / 100);
    const zeroPriced = positions.filter((p) => lineNet(p) <= 0).length;

    const cats = new Set(positions.map((p) => p.billing_category || "customer"));
    const internCats = ["warranty", "gw", "internal"].filter((c) => cats.has(c));
    const isSplit = internCats.length > 0;

    // Gutschein-Rabatt-Vorschau (nur auf Kunden-Positionen)
    const customerNet = positions
        .filter((p) => (p.billing_category || "customer") === "customer")
        .reduce((s, p) => s + lineNet(p), 0);
    const voucherDiscNet = voucherInfo
        ? (voucherInfo.discount_type === "eur"
            ? Math.min(voucherInfo.discount_value, customerNet)
            : customerNet * voucherInfo.discount_value / 100)
        : 0;

    // Jul-2026 (User-Wunsch) — eigener Gesamtbetrag: Differenz zum berechneten
    // Brutto wird in der Rechnung als Rabatt-Position ausgewiesen.
    const [customTotal, setCustomTotal] = useState("");
    const computedGross = Math.max(0, (order.totals?.total_gross || 0) - voucherDiscNet * 1.19);
    const customVal = parseFloat(String(customTotal).replace(",", "."));
    const customValid = customTotal !== "" && !Number.isNaN(customVal) && customVal > 0 && customVal < computedGross - 0.009;
    const customDiscount = customValid ? computedGross - customVal : 0;
    const customTooHigh = customTotal !== "" && !Number.isNaN(customVal) && customVal >= computedGross - 0.009;

    // Nicht übernommene (gestoppte) Zeiterfassung → AW-Vorschlag (1 AW = 6 min)
    const openMin = (timesheet?.items || [])
        .filter((e) => e.stopped_at && !e.invoiced)
        .reduce((s, e) => s + (Number(e.duration_min) || 0), 0);
    const openAw = Math.ceil(openMin / 6);

    const takeTimeAsPosition = async () => {
        if (!awRate) return;
        setTakingTime(true);
        try {
            const res = await api.post(`/work-orders/${order.id}/timesheet/to-position`, {
                aw_satz_eur: awRate, vat_rate: 19,
            });
            toast.success(`${res.data.total_min} min als ${res.data.aw} AW-Lohnposition übernommen`);
            await reloadOrder();
            const r = await api.get(`/work-orders/${order.id}/timesheet`);
            setTimesheet(r.data);
        } catch (e) { toast.error(e.response?.data?.detail || "Übernahme fehlgeschlagen"); }
        setTakingTime(false);
    };

    const custLabel = customer?.type === "business" && customer?.company_name
        ? customer.company_name
        : `${customer?.first_name || ""} ${customer?.last_name || ""}`.trim() || "—";
    // Aug-2026 (User-Wunsch): Adress-Warnung + Schnellkorrektur beim Abrechnen —
    // vollen Kundendatensatz laden (customer-Prop ist oft nur ein Brief-Objekt).
    const [fullCust, setFullCust] = useState(null);
    const [addrFixOpen, setAddrFixOpen] = useState(false);
    useEffect(() => {
        if (!initialOrder.customer_id) return;
        api.get(`/customers/${initialOrder.customer_id}`)
            .then((r) => setFullCust(r.data))
            .catch(() => setFullCust(null));
    }, [initialOrder.customer_id]);
    const addrIssues = addressIssues(fullCust);
    const veh = order.vehicle || {};
    const vehLabel = [veh.brand, veh.model].filter(Boolean).join(" ") || "—";

    return (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" data-testid="invoice-preview-modal">
            <div className="bg-white max-w-3xl w-full rounded-sm shadow-xl max-h-[92vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 bg-[#111827] text-white rounded-t-sm">
                    <div>
                        <div className="font-display font-black text-lg">Rechnung erstellen · {order.order_number}</div>
                        <div className="text-[11px] text-gray-300">Bitte prüfen: Sind alle Positionen enthalten?</div>
                    </div>
                    <button onClick={onClose} className="p-1 rounded-sm hover:bg-white/10" data-testid="invoice-preview-close">
                        <X className="w-4 h-4" />
                    </button>
                </div>

                <div className="p-5 space-y-4 overflow-y-auto flex-1">
                    <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm">
                        <div><span className="text-gray-400 text-[10px] uppercase tracking-wider block">Kunde</span>{custLabel}</div>
                        <div>
                            <span className="text-gray-400 text-[10px] uppercase tracking-wider block">Fahrzeug</span>
                            {vehLabel}{veh.license_plate ? ` · ${veh.license_plate}` : ""}
                        </div>
                        <div><span className="text-gray-400 text-[10px] uppercase tracking-wider block">Positionen</span><span className="font-bold">{positions.length}</span></div>
                    </div>

                    {addrIssues.length > 0 && (
                        <div className="text-[11px] font-bold rounded-sm px-2 py-1.5 border bg-amber-50 border-amber-300 text-amber-900" data-testid="wo-billing-addr-warning">
                            <div className="flex items-start gap-1.5">
                                <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                                <span className="flex-1">
                                    Kundenanschrift unvollständig — es fehlt: <b>{addrIssues.join(", ")}</b>.
                                    Bitte vor dem Abrechnen ergänzen (Rechnung wird sonst mit unvollständiger Adresse gedruckt).
                                </span>
                                {!addrFixOpen && (
                                    <button type="button" onClick={() => setAddrFixOpen(true)}
                                        className="shrink-0 px-2 py-1 rounded-sm bg-[#111827] text-white hover:bg-black text-[10px] font-bold"
                                        data-testid="wo-addr-quickfix-btn">
                                        Jetzt ergänzen
                                    </button>
                                )}
                            </div>
                            {addrFixOpen && (
                                <CustomerAddressQuickFix customer={fullCust}
                                    onSaved={(u) => { setFullCust(u); setAddrFixOpen(false); }}
                                    onClose={() => setAddrFixOpen(false)} />
                            )}
                        </div>
                    )}

                    <div className="border border-gray-200 rounded-sm overflow-hidden" data-testid="invoice-preview-positions">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="bg-gray-50 text-[10px] uppercase tracking-wider text-gray-500">
                                    <th className="text-left px-3 py-2 font-bold">Position</th>
                                    <th className="text-right px-2 py-2 font-bold">Menge</th>
                                    <th className="text-right px-2 py-2 font-bold">EP netto</th>
                                    <th className="text-right px-3 py-2 font-bold">Gesamt brutto</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {positions.map((p, i) => {
                                    const bc = p.billing_category || "customer";
                                    return (
                                        <tr key={i} data-testid={`invoice-preview-row-${i}`}>
                                            <td className="px-3 py-2">
                                                <div className="flex items-center gap-1.5">
                                                    <Badge className={`shrink-0 text-[9px] font-bold uppercase rounded-sm ${p.type === "labour" ? "bg-blue-600 text-white" : "bg-gray-200 text-gray-800"}`}>
                                                        {p.type === "labour" ? "Lohn" : "Teil"}
                                                    </Badge>
                                                    <span className={lineNet(p) <= 0 ? "text-amber-800" : ""}>{p.description || <span className="text-gray-400 italic">ohne Beschreibung</span>}</span>
                                                    {bc !== "customer" && (
                                                        <span className="inline-flex items-center gap-1 text-[9px] font-bold uppercase text-gray-600">
                                                            <span className="w-2 h-2 rounded-full" style={{ backgroundColor: (ORDER_CATEGORY_META[bc] || {}).hex }} />
                                                            {CAT_LABEL[bc]}
                                                        </span>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="px-2 py-2 text-right font-mono-num">{p.quantity} {p.unit || ""}</td>
                                            <td className="px-2 py-2 text-right font-mono-num">{fmtEur(p.unit_price_net)}</td>
                                            <td className="px-3 py-2 text-right font-mono-num font-bold">{fmtEur(lineGross(p))}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>

                    {zeroPriced > 0 && (
                        <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-sm px-3 py-2 text-[12px] text-amber-900" data-testid="invoice-preview-zero-warning">
                            <AlertTriangle className="w-4 h-4 shrink-0" />
                            {zeroPriced} Position{zeroPriced === 1 ? "" : "en"} ohne Preis (0,00 €) — bitte prüfen, ob das beabsichtigt ist.
                        </div>
                    )}

                    {openMin > 0 && (
                        <div className="bg-teal-50 border border-teal-200 rounded-sm px-3 py-2.5" data-testid="invoice-preview-time-hint">
                            <div className="flex items-center gap-2 text-[12px] text-teal-900 font-bold">
                                <Timer className="w-4 h-4 shrink-0" />
                                Nicht übernommene Zeiterfassung: {openMin} min ≈ {openAw} AW
                                {awRate ? ` (= ${fmtEur(openAw * awRate)} netto bei ${fmtEur(awRate)}/AW)` : ""}
                            </div>
                            {awRate ? (
                                <Button size="sm" variant="outline" disabled={takingTime} onClick={takeTimeAsPosition}
                                    className="mt-2 h-7 text-[11px] rounded-sm border-teal-400 text-teal-900 hover:bg-teal-100 font-bold"
                                    data-testid="invoice-preview-take-time">
                                    {takingTime ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Check className="w-3 h-3 mr-1" />}
                                    Als AW-Lohnposition übernehmen
                                </Button>
                            ) : (
                                <div className="text-[11px] text-teal-800 mt-1">Kein AW-Satz hinterlegt (WWS → Kalkulation) — Übernahme über die Stempeluhr möglich.</div>
                            )}
                        </div>
                    )}

                    {isSplit && (
                        <div className="bg-indigo-50 border border-indigo-200 rounded-sm px-3 py-2.5 text-[12px] text-indigo-900" data-testid="invoice-preview-split-hint">
                            <div className="font-bold mb-1">Auftrag enthält interne Positionen — Abrechnung wird aufgeteilt:</div>
                            <ul className="list-disc ml-4 space-y-0.5">
                                <li>Kundenrechnung nur für „Kunde&quot;-Positionen{cats.has("customer") ? "" : " (keine vorhanden)"}</li>
                                <li>Je interne Kategorie ein Folge-Auftrag: {internCats.map((c) => CAT_LABEL[c]).join(", ")}</li>
                            </ul>
                        </div>
                    )}

                    {/* Nov-2026 (User-Wunsch) — Gutschein, eigener Gesamtbetrag und
                        Summe nebeneinander in einer Zeile. */}
                    <div className="grid grid-cols-1 lg:grid-cols-[1fr_1fr_auto] gap-3 items-stretch">
                        {(cats.has("customer") || !isSplit) ? (
                            <div className="bg-pink-50/60 border border-pink-200 rounded-sm px-3 py-2.5" data-testid="invoice-preview-voucher">
                                <div className="flex items-center gap-2 text-[12px] font-bold text-pink-900 mb-1.5">
                                    <TicketPercent className="w-4 h-4 shrink-0" /> Gutschein einlösen
                                    {voucherChecking && <Loader2 className="w-3 h-3 animate-spin" />}
                                </div>
                                {voucherInfo ? (
                                    <div className="flex items-center justify-between gap-2 text-[12px]" data-testid="invoice-preview-voucher-applied">
                                        <span className="font-bold text-pink-900">
                                            {voucherInfo.code} · {voucherInfo.discount_type === "eur" ? fmtEur(voucherInfo.discount_value) : `${voucherInfo.discount_value} %`}
                                            {" "}→ −{fmtEur(voucherDiscNet)} netto
                                        </span>
                                        <button type="button" onClick={() => { setVoucherInfo(null); setVoucherCode(""); }}
                                            className="text-[11px] font-bold text-pink-700 underline hover:no-underline"
                                            data-testid="invoice-preview-voucher-remove">entfernen</button>
                                    </div>
                                ) : (
                                    <>
                                        <input
                                            value={voucherCode}
                                            onChange={(e) => setVoucherCode(e.target.value)}
                                            placeholder="Code, z. B. GUT-A1B2C3"
                                            className="w-full h-8 rounded-sm border border-pink-300 bg-white px-2 text-[12px] font-mono uppercase focus:outline-none focus:ring-1 focus:ring-pink-400"
                                            data-testid="invoice-preview-voucher-input"
                                        />
                                        {custVouchers.length > 0 && (
                                            <div className="flex flex-wrap gap-1.5 mt-1.5">
                                                {custVouchers.map((v) => (
                                                    <button key={v.code} type="button"
                                                        onClick={() => { setVoucherCode(v.code); checkVoucher(v.code); }}
                                                        className="text-[10px] font-bold px-2 py-1 rounded-full border border-pink-300 bg-white text-pink-800 hover:bg-pink-100 transition-colors"
                                                        data-testid={`invoice-preview-voucher-chip-${v.code}`}>
                                                        {v.code} · {v.discount_type === "eur" ? fmtEur(v.discount_value) : `${v.discount_value} %`}
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </>
                                )}
                                <div className="text-[10px] text-pink-800 mt-1">Wird beim Abrechnen als Rabattposition in die Kundenrechnung übernommen.</div>
                            </div>
                        ) : <div className="hidden lg:block" />}

                        {/* Jul-2026 (User-Wunsch) — eigener Gesamtbetrag → Rabatt */}
                        <div className="bg-emerald-50/60 border border-emerald-200 rounded-sm px-3 py-2.5" data-testid="invoice-preview-custom-total">
                            <div className="text-[12px] font-bold text-emerald-900 mb-1.5">Eigener Gesamtbetrag (brutto) — optional</div>
                            <div className="flex items-center gap-2 flex-wrap">
                                <input
                                    type="text"
                                    inputMode="decimal"
                                    value={customTotal}
                                    onChange={(e) => setCustomTotal(e.target.value)}
                                    placeholder={`z. B. ${Math.floor(computedGross)} statt ${computedGross.toFixed(2).replace(".", ",")}`}
                                    className={`w-40 h-8 rounded-sm border bg-white px-2 text-[13px] font-mono-num text-right focus:outline-none focus:ring-1 ${customTooHigh ? "border-red-400 focus:ring-red-400" : "border-emerald-300 focus:ring-emerald-400"}`}
                                    data-testid="invoice-preview-custom-total-input"
                                />
                                <span className="text-[12px] text-emerald-900">€</span>
                                {customDiscount > 0 && (
                                    <span className="text-[11px] font-bold text-emerald-800 bg-white border border-emerald-300 rounded-sm px-2 py-1">
                                        Differenz −{fmtEur(customDiscount)} wird als Rabatt ausgewiesen
                                    </span>
                                )}
                                {customTooHigh && (
                                    <span className="text-[11px] font-bold text-red-700" data-testid="invoice-preview-custom-total-error">
                                        Betrag muss unter dem berechneten Gesamtbetrag liegen
                                    </span>
                                )}
                            </div>
                            <div className="text-[10px] text-emerald-800 mt-1">Die Differenz zum berechneten Betrag erscheint in der Rechnung als eigene Rabatt-Position.</div>
                            {servicePrices.length > 0 && (
                                <div className="mt-2 pt-2 border-t border-emerald-200" data-testid="invoice-preview-service-prices">
                                    <div className="text-[10px] font-bold uppercase tracking-wider text-emerald-900 mb-1">Aktionspreise (Werkstatt-Servicepreise)</div>
                                    <div className="flex flex-wrap gap-1.5 max-h-24 overflow-y-auto">
                                        {servicePrices.map((a) => (
                                            <button key={a.id} type="button"
                                                onClick={() => setCustomTotal(String(a.sell_price_gross).replace(".", ","))}
                                                title={a.notes || a.name}
                                                className="text-[10px] font-bold px-2 py-1 rounded-full border border-emerald-300 bg-white text-emerald-800 hover:bg-emerald-100 transition-colors"
                                                data-testid={`invoice-preview-service-price-${a.sku}`}>
                                                {a.name} · {fmtEur(a.sell_price_gross)}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="flex items-end justify-end lg:pl-2">
                            <div className="text-right space-y-0.5">
                                <div className="text-[12px] text-gray-500">netto {fmtEur(order.totals?.total_net)} · MwSt {fmtEur(order.totals?.total_vat)}</div>
                                {voucherDiscNet > 0 && (
                                    <div className="text-[12px] font-bold text-pink-700" data-testid="invoice-preview-voucher-discount">Gutschein: −{fmtEur(voucherDiscNet)} netto (−{fmtEur(voucherDiscNet * 1.19)} brutto)</div>
                                )}
                                {customDiscount > 0 && (
                                    <div className="text-[12px] font-bold text-emerald-700" data-testid="invoice-preview-custom-discount">Rabatt (vereinbarter Gesamtpreis): −{fmtEur(customDiscount)} brutto</div>
                                )}
                                <div className="font-mono-num font-black text-2xl" data-testid="invoice-preview-total-gross">{fmtEur(customValid ? customVal : Math.max(0, (order.totals?.total_gross || 0) - voucherDiscNet * 1.19))}</div>
                            </div>
                        </div>
                    </div>

                    {/* Aug-2026 (User-Wunsch) — Zahlart: Pflichtfeld, Standard EC;
                        Zahnrad konfiguriert sichtbare Zahlarten + Druck-Text. */}
                    <div className="bg-blue-50/60 border border-blue-200 rounded-sm px-3 py-2.5" data-testid="invoice-preview-payment">
                        <div className="flex items-center justify-between mb-1.5">
                            <div className="text-[12px] font-bold text-blue-900">Zahlart <span className="text-red-600">*</span> <span className="font-normal text-blue-800">(Pflichtfeld)</span></div>
                            <button type="button" onClick={() => setCfgOpen((v) => !v)}
                                title="Zahlarten konfigurieren: Sichtbarkeit + Druck-Text auf der Rechnung"
                                className="p-1 rounded-sm text-blue-800 hover:bg-blue-100"
                                data-testid="invoice-pay-config-gear">
                                <Settings className="w-4 h-4" />
                            </button>
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                            {enabledPayOptions.map(([k, label]) => (
                                <button key={k} type="button"
                                    onClick={() => setPayCode(k)}
                                    className={`text-[11px] font-bold px-2.5 py-1.5 rounded-full border transition-colors ${payCode === k
                                        ? "border-blue-600 bg-blue-600 text-white"
                                        : "border-blue-300 bg-white text-blue-900 hover:bg-blue-100"}`}
                                    data-testid={`invoice-preview-pay-${k}`}>{label}</button>
                            ))}
                        </div>
                        <div className="text-[10px] text-blue-800 mt-1">
                            Wird immer auf der Rechnung als „Zahlung: …&quot; eingedruckt{(payCfg?.print_texts || {})[payCode] ? ` — Druck-Text: „${payCfg.print_texts[payCode]}"` : ""}.
                            {payCode === "ueberweisung" && " Die hinterlegte Bankverbindung wird mit angedruckt."}
                        </div>
                        {cfgOpen && payCfg && (
                            <div className="mt-2 border border-blue-300 rounded-sm bg-white p-2.5 space-y-1.5" data-testid="invoice-pay-config-panel">
                                <div className="text-[11px] font-bold text-blue-900">Zahlarten konfigurieren <span className="font-normal text-gray-500">(gilt org-weit)</span></div>
                                {PAY_OPTIONS.map(([k, label]) => (
                                    <div key={k} className="flex items-center gap-2">
                                        <label className="flex items-center gap-1.5 w-56 shrink-0 text-[11px] font-bold text-gray-800">
                                            <input type="checkbox"
                                                checked={(payCfg.enabled || []).includes(k)}
                                                disabled={k === "ec"}
                                                onChange={(e) => setPayCfg((c) => ({
                                                    ...c,
                                                    enabled: e.target.checked
                                                        ? [...(c.enabled || []), k]
                                                        : (c.enabled || []).filter((x) => x !== k),
                                                }))}
                                                data-testid={`pay-cfg-enabled-${k}`} />
                                            {label}{k === "ec" && <span className="text-[9px] text-gray-500">(Standard, fix)</span>}
                                        </label>
                                        <input type="text"
                                            value={(payCfg.print_texts || {})[k] || ""}
                                            onChange={(e) => setPayCfg((c) => ({
                                                ...c, print_texts: { ...(c.print_texts || {}), [k]: e.target.value },
                                            }))}
                                            placeholder={`Druck-Text (leer = „${label}")`}
                                            className="flex-1 border border-gray-300 rounded-sm px-2 py-1 text-[11px]"
                                            data-testid={`pay-cfg-text-${k}`} />
                                    </div>
                                ))}
                                <div className="flex justify-end gap-2 pt-1">
                                    <Button size="sm" variant="outline" className="h-7 text-[11px] rounded-sm" onClick={() => setCfgOpen(false)}>Abbrechen</Button>
                                    <Button size="sm" disabled={cfgSaving} className="h-7 text-[11px] rounded-sm bg-blue-600 hover:bg-blue-700 text-white" onClick={savePayCfg} data-testid="pay-cfg-save">
                                        {cfgSaving ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : null} Speichern
                                    </Button>
                                </div>
                            </div>
                        )}
                    </div>

                    {order.status !== "completed" && (
                        <div className="text-[11px] text-gray-500">
                            Beim Erstellen wird der Auftrag automatisch abgeschlossen — verknüpfte Lagerteile werden abgebucht.
                        </div>
                    )}
                </div>

                <div className="px-5 py-3 border-t border-gray-200 flex flex-wrap gap-2 justify-between items-center">
                    <Button variant="outline" onClick={onEdit} className="rounded-sm text-[12px] font-bold" data-testid="invoice-preview-edit">
                        <Edit2 className="w-3.5 h-3.5 mr-1.5" /> Zurück zum Bearbeiten
                    </Button>
                    <div className="flex gap-2 flex-wrap justify-end">
                        {/* Nov-2026 (User-Wunsch): „Als Entwurf"-Button entfernt — die
                            Vorschau IST der Entwurf; ändern über „Zurück zum Bearbeiten". */}
                        <Button disabled={busy || customTooHigh || !payCode} onClick={() => onConfirm(isSplit ? "split" : "direct", order, voucherInfo?.code, customValid ? customVal : null, payCode)}
                            className="rounded-sm text-[12px] font-black bg-emerald-600 hover:bg-emerald-700 text-white"
                            data-testid="invoice-preview-confirm">
                            {busy ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <FileText className="w-3.5 h-3.5 mr-1.5" />}
                            {isSplit ? "Aufteilen & abrechnen" : "Rechnung erstellen & finalisieren"}
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
}
